import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash, FaTimes, FaSave, FaSearch, FaEye, FaPrint } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import Loading from '../../components/common/Loading';
import './Billing.css';

const AccountsLedger = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [ledgers, setLedgers] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const [search, setSearch] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [viewingEntry, setViewingEntry] = useState(null);

  const [formData, setFormData] = useState({
    account: '',
    date: '',
    description: '',
    debit: 0,
    credit: 0
  });

  const formatDate = (inputDate) => {
    if (!inputDate) return '';
    const dateObj = new Date(inputDate);
    if (Number.isNaN(dateObj.getTime())) return '';
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = String(dateObj.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    loadAccounts();
    loadLedgers();
  }, []);

  useEffect(() => { setCurrentPage(1); }, [search, accountFilter, fromDate, toDate]);

  const loadAccounts = async () => {
    try {
      const res = await api.get('/api/accounts', { params: { limit: 1000 } });
      const accs = res.data?.accounts || [];
      accs.sort((a, b) => (a.accountName || '').toLowerCase().localeCompare((b.accountName || '').toLowerCase()));
      setAccounts(accs);
    } catch (e) {
      // noop
    }
  };

  const loadLedgers = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      if (accountFilter) params.append('account', accountFilter);
      if (fromDate) params.append('from', fromDate);
      if (toDate) params.append('to', toDate);
      if (search) params.append('search', search);
      const res = await api.get(`/api/accounts-ledger?${params.toString()}`);
      setLedgers(res.data?.ledgers || []);
    } catch (e) {
      setError('Failed to load ledger entries');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingEntry(null);
    setFormData({ account: '', date: '', description: '', debit: 0, credit: 0 });
    setShowModal(true);
    setError('');
  };

  const openEditModal = (entry) => {
    setEditingEntry(entry);
    setFormData({
      account: entry.account?._id || entry.account,
      date: entry.date ? String(new Date(entry.date).toISOString().split('T')[0]) : '',
      description: entry.description || '',
      debit: Number(entry.debit || 0),
      credit: Number(entry.credit || 0)
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingEntry(null);
    setError('');
  };

  const closeViewModal = () => {
    setViewingEntry(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const payload = {
        account: formData.account,
        date: formData.date || undefined,
        description: formData.description,
        debit: Number(formData.debit || 0),
        credit: Number(formData.credit || 0)
      };
      if (editingEntry) {
        const res = await api.put(`/api/accounts-ledger/${editingEntry._id}`, payload);
        const updated = res.data?.ledger;
        setLedgers(prev => prev.map(l => l._id === updated._id ? updated : l));
      } else {
        const res = await api.post('/api/accounts-ledger', payload);
        setLedgers(prev => [res.data?.ledger, ...prev]);
      }
      closeModal();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save ledger entry');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this ledger entry?')) return;
    try {
      await api.delete(`/api/accounts-ledger/${entryId}`);
      setLedgers(prev => prev.filter(l => l._id !== entryId));
    } catch (err) {
      setError('Failed to delete ledger entry');
    }
  };

  const handlePrintEntry = (entry) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Ledger Entry - ${entry._id}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #444; padding-bottom: 15px; }
            .header h1 { margin: 0; color: #2c3e50; text-transform: uppercase; letter-spacing: 2px; }
            .details-container { display: flex; justify-content: space-between; margin-bottom: 30px; background: #f9f9f9; padding: 20px; border-radius: 8px; }
            .details-column { flex: 1; }
            .details-column p { margin: 8px 0; }
            .details-column strong { color: #555; width: 120px; display: inline-block; }
            .financials { margin-top: 30px; border: 1px solid #eee; border-radius: 8px; overflow: hidden; }
            .financials-row { display: flex; justify-content: space-between; padding: 15px 20px; border-bottom: 1px solid #eee; }
            .financials-row:last-child { border-bottom: none; background: #f2f4f6; }
            .financials-label { font-weight: 600; color: #2c3e50; }
            .financials-value { font-family: 'Courier New', Courier, monospace; font-weight: bold; font-size: 1.1em; }
            .total-row { color: #d35400; font-size: 1.2em; }
            .footer { margin-top: 50px; text-align: center; font-size: 0.85em; color: #7f8c8d; border-top: 1px solid #eee; padding-top: 15px; }
            @media print {
              body { padding: 20px; }
              .details-container { background: #fff; border: 1px solid #eee; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Ledger Entry Details</h1>
          </div>
          <div class="details-container">
            <div class="details-column">
              <p><strong>Date:</strong> ${formatDate(entry.date)}</p>
              <p><strong>Account:</strong> ${entry.account?.accountName || 'N/A'}</p>
              <p><strong>Account Code:</strong> ${entry.account?.accountCode || 'N/A'}</p>
            </div>
            <div class="details-column">
              <p><strong>Description:</strong> ${entry.description || '-'}</p>
              <p><strong>Printed On:</strong> ${new Date().toLocaleString()}</p>
            </div>
          </div>
          <div class="financials">
            <div class="financials-row">
              <span class="financials-label">Debit Amount:</span>
              <span class="financials-value">₹${Number(entry.debit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            <div class="financials-row">
              <span class="financials-label">Credit Amount:</span>
              <span class="financials-value">₹${Number(entry.credit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            <div class="financials-row total-row">
              <span class="financials-label">Due Amount (Balance):</span>
              <span class="financials-value">₹${Number(entry.runningBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
          <div class="footer">
            <p>This is a computer-generated document.</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handlePrintReport = () => {
    const printWindow = window.open('', '_blank');

    let accountInfo = '';
    if (accountFilter) {
      const acc = accounts.find(a => a._id === accountFilter);
      accountInfo = `<span><strong>Account:</strong> ${acc ? acc.accountName : accountFilter}</span>`;
    }

    let periodInfo = '';
    if (fromDate || toDate) {
      periodInfo = `<span><strong>Period:</strong> ${fromDate || 'Beginning'} to ${toDate || 'End'}</span>`;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Accounts Ledger Report</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 30px; color: #333; }
            .header { text-align: center; margin-bottom: 30px; }
            .header h1 { margin: 0; color: #2c3e50; font-size: 24px; text-transform: uppercase; }
            .report-meta { display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 0.9em; border-bottom: 1px solid #eee; padding-bottom: 10px; }
            .info-badges { display: flex; gap: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th { background-color: #f8f9fa; color: #2c3e50; font-weight: 600; border: 1px solid #dee2e6; padding: 12px 8px; text-align: left; }
            td { border: 1px solid #dee2e6; padding: 10px 8px; font-size: 0.85em; }
            tr:nth-child(even) { background-color: #fdfdfd; }
            .text-right { text-align: right; }
            .amount { font-family: 'Courier New', Courier, monospace; }
            .footer { margin-top: 30px; display: flex; justify-content: space-between; font-size: 0.8em; color: #7f8c8d; }
            @media print {
              th { background-color: #f8f9fa !important; -webkit-print-color-adjust: exact; }
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Accounts Ledger Report</h1>
          </div>
          <div class="report-meta">
            <div class="info-badges">
              ${accountInfo}
              ${periodInfo}
            </div>
            <div><strong>Generated:</strong> ${new Date().toLocaleString()}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th style="width: 80px;">Date</th>
                <th>Account</th>
                <th>Description</th>
                <th class="text-right" style="width: 100px;">Debit</th>
                <th class="text-right" style="width: 100px;">Credit</th>
                <th class="text-right" style="width: 100px;">Balance</th>
              </tr>
            </thead>
            <tbody>
              ${filtered.map(entry => `
                <tr>
                  <td>${formatDate(entry.date)}</td>
                  <td>${entry.account?.accountName || ''}</td>
                  <td>${entry.description || '-'}</td>
                  <td class="text-right amount">₹${Number(entry.debit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td class="text-right amount">₹${Number(entry.credit || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td class="text-right amount">₹${Number(entry.runningBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="footer">
            <span>Total Entries: ${filtered.length}</span>
            <span>&copy; ${new Date().getFullYear()} Billing & Inventory System</span>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const filtered = ledgers.filter(l => {
    const q = search.toLowerCase();
    const text = [
      l.description,
      l.account?.accountName,
      l.account?.accountCode
    ].filter(Boolean).join(' ').toLowerCase();
    const accountOk = !accountFilter || (l.account && (l.account._id === accountFilter || l.account === accountFilter));
    let dateOk = true;
    if (fromDate) dateOk = dateOk && new Date(l.date) >= new Date(fromDate);
    if (toDate) dateOk = dateOk && new Date(l.date) <= new Date(toDate);
    return text.includes(q) && accountOk && dateOk;
  }).sort((a, b) => {
    const nameA = (a.account?.accountName || '').toLowerCase();
    const nameB = (b.account?.accountName || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const pageStart = (currentPage - 1) * itemsPerPage;
  const pageEnd = pageStart + itemsPerPage;
  const paginated = filtered.slice(pageStart, pageEnd);

  if (loading && ledgers.length === 0) return <Loading />;

  return (
    <div className="billing-container">
      <div className="billing-header">
        <div>
          <h1 className="billing-title">Accounts Ledger</h1>
          <p className="billing-subtitle">Track debits, credits and Due Amounts</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn-secondary" onClick={handlePrintReport}>
            <FaPrint /> Print Report
          </button>
          <button className="add-billing-btn" onClick={openCreateModal}>
            <FaPlus /> Add Entry
          </button>
        </div>
      </div>

      <div className="filters-section">
        <input
          type="text"
          placeholder="Search by description or account..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="search-input"
        />
        <div className="filter-row">
          <select
            value={accountFilter}
            onChange={e => setAccountFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Accounts</option>
            {accounts.map(a => (
              <option key={a._id} value={a._id}>{a.accountCode} - {a.accountName}</option>
            ))}
          </select>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="filter-select" />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="filter-select" />
          <button onClick={loadLedgers} className="refresh-btn">
            Refresh
          </button>
        </div>
      </div>

      <div className="billing-table-wrapper">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ color: '#6c757d', fontSize: '0.9rem' }}>
            Showing {filtered.length === 0 ? 0 : (pageStart + 1)}-{Math.min(filtered.length, pageEnd)} of {filtered.length}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label htmlFor="pageSize" style={{ color: '#6c757d', fontSize: '0.9rem' }}>Rows per page:</label>
            <select
              id="pageSize"
              className="filter-select"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              style={{ minWidth: 100 }}
            >
              {[10, 20, 50, 100].map(sz => (
                <option key={sz} value={sz}>{sz}</option>
              ))}
            </select>
            <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
            <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>Page {currentPage} of {totalPages}</span>
            <button className="btn-secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>Next</button>
          </div>
        </div>

        <table className="billing-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Account</th>
              <th>Description</th>
              <th>Debit</th>
              <th>Credit</th>
              <th>Due Amount</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center' }}>No ledger entries found.</td>
              </tr>
            ) : (
              paginated.map(entry => (
                <tr key={entry._id}>
                  <td>{formatDate(entry.date)}</td>
                  <td>{entry.account?.accountName || 'N/A'}</td>
                  <td>{entry.description || '-'}</td>
                  <td>₹{Number(entry.debit || 0).toFixed(2)}</td>
                  <td>₹{Number(entry.credit || 0).toFixed(2)}</td>
                  <td>₹{Number(entry.runningBalance || 0).toFixed(2)}</td>
                  <td>
                    <div className="action-buttons-row">
                      <button onClick={() => setViewingEntry(entry)} className="action-btn view" title="View Entry">
                        <FaEye /> View
                      </button>
                      <button onClick={() => handlePrintEntry(entry)} className="action-btn print" title="Print Entry">
                        <FaPrint /> Print
                      </button>
                      <button onClick={() => openEditModal(entry)} className="action-btn edit" title="Edit Entry">
                        <FaEdit /> Edit
                      </button>
                      <button onClick={() => handleDelete(entry._id)} className="action-btn delete" title="Delete Entry">
                        <FaTrash /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {(showModal) && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingEntry ? 'Edit Ledger Entry' : 'Add Ledger Entry'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            {error && (
              <div className="error-message">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="billing-form">
              <div className="form-section">
                <h3>Entry Details</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="account">Account *</label>
                    <select id="account" name="account" value={formData.account} onChange={handleInputChange} required>
                      <option value="">Select an account</option>
                      {accounts.map(acc => (
                        <option key={acc._id} value={acc._id}>{acc.accountCode} - {acc.accountName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="date">Date</label>
                    <input type="date" id="date" name="date" value={formData.date} onChange={handleInputChange} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="debit">Debit</label>
                    <input type="number" id="debit" name="debit" value={formData.debit} onChange={handleInputChange} min="0" step="0.01" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="credit">Credit</label>
                    <input type="number" id="credit" name="credit" value={formData.credit} onChange={handleInputChange} min="0" step="0.01" />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea id="description" name="description" value={formData.description} onChange={handleInputChange} rows="3" placeholder="Enter description" />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={closeModal} disabled={loading}>
                  <FaTimes /> Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  <FaSave /> {loading ? 'Saving...' : (editingEntry ? 'Update Entry' : 'Add Entry')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewingEntry && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ledger Entry Details</h2>
              <button className="modal-close" onClick={closeViewModal}>
                <FaTimes />
              </button>
            </div>
            <div className="billing-view-content">
              <div className="billing-info-section">
                <div className="detail-group">
                  <h3>Entry Information</h3>
                  <div className="detail-item"><label>Date:</label><span>{viewingEntry.date ? new Date(viewingEntry.date).toLocaleString() : '-'}</span></div>
                  <div className="detail-item"><label>Account:</label><span>{viewingEntry.account?.accountName || '-'}</span></div>
                  <div className="detail-item"><label>Account Code:</label><span>{viewingEntry.account?.accountCode || '-'}</span></div>
                  <div className="detail-item"><label>Description:</label><span>{viewingEntry.description || '-'}</span></div>
                </div>
                <div className="detail-group">
                  <h3>Amounts</h3>
                  <div className="detail-item"><label>Debit:</label><span>₹{Number(viewingEntry.debit || 0).toFixed(2)}</span></div>
                  <div className="detail-item"><label>Credit:</label><span>₹{Number(viewingEntry.credit || 0).toFixed(2)}</span></div>
                  <div className="detail-item total-amount"><label>Due Amount:</label><span>₹{Number(viewingEntry.runningBalance || 0).toFixed(2)}</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountsLedger;


